import { useState, useEffect, useMemo } from "react";
import { PropertyFilterBar } from "../../shared/components/property-filter-bar";
import { LoadingSpinner } from "../../shared/components/loading-spinner";
import { Link, useSearchParams } from "react-router-dom";
import { PropertyCard } from "./property-card";

export const ExploreProperties = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [propertyData, setPropertyData] = useState([]);
  const [descriptionData, setDescriptionData] = useState([]);

  const [searchParams] = useSearchParams();

  // Filters
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedPropertyType, setSelectedPropertyType] = useState("");
  const [selectedNumberOfRooms, setSelectedNumberOfRooms] = useState("");
  const [selectedPropertyAge, setSelectedPropertyAge] = useState("");
  const [priceRange, setPriceRange] = useState([0, 1000000]);
  const [maxPrice, setMaxPrice] = useState(1000000);

  // Fetch properties and descriptions together
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [propertiesRes, descriptionsRes] = await Promise.all([
          fetch("https://api.therealrealtor.lk/api/get-all-properties"),
          fetch("https://api.therealrealtor.lk/api/get-property-descriptions")
        ]);

        if (!propertiesRes.ok) throw new Error("Failed to fetch properties");
        if (!descriptionsRes.ok) throw new Error("Failed to fetch descriptions");

        const properties = await propertiesRes.json();
        const descriptions = await descriptionsRes.json();

        setPropertyData(properties);
        setDescriptionData(descriptions);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Initialize filters from URL parameters
  useEffect(() => {
    const location = searchParams.get("location");
    const category = searchParams.get("category");
    const minPrice = searchParams.get("minPrice");
    const maxPriceParam = searchParams.get("maxPrice");

    if (location) setSelectedLocation(location);
    if (category) setSelectedPropertyType(category);
    if (minPrice && maxPriceParam) setPriceRange([Number(minPrice), Number(maxPriceParam)]);
  }, [searchParams]);

  // Set max price dynamically for rent listings
  useEffect(() => {
    if (propertyData.length > 0) {
      const sellData = propertyData.filter((p) => p.listingType === "sell");
      const highestPrice = Math.max(...sellData.map((p) => parseFloat(p.price) || 0), 1000000);

      if (!searchParams.get("minPrice") && !searchParams.get("maxPrice")) {
        setPriceRange([0, highestPrice]);
        setMaxPrice(highestPrice);
      }
    }
  }, [propertyData, searchParams]);

  // Memoize description mapping for performance
  const descriptionsMap = useMemo(() => {
    const map = {};
    descriptionData.forEach((d) => {
      if (!map[d.property_id]) map[d.property_id] = [];
      map[d.property_id].push(d.description);
    });
    return map;
  }, [descriptionData]);

  // Memoize filtered properties
  const filteredProperties = useMemo(() => {
    const sellData = propertyData.filter((p) => p.listingType === "sell") || [];
    return sellData.filter((property) => {
      // Parse safely
      const propPrice = parseFloat(property.price) || 0;
      const propRooms = parseInt(property.numberOfRooms) || 0;
      const selectedRooms = parseInt(selectedNumberOfRooms);

      const isWithinLocation = selectedLocation
        ? property.location?.trim().toLowerCase() === selectedLocation.trim().toLowerCase()
        : true;

      const isWithinPriceRange = propPrice >= priceRange[0] && propPrice <= priceRange[1];

      const isOfSelectedPropertyType = selectedPropertyType
        ? property.propertyType?.toString().toLowerCase() === selectedPropertyType.toLowerCase()
        : true;

      const isSelectedPropertyAge = selectedPropertyAge
        ? property.age?.toString() === selectedPropertyAge
        : true;

      const isNumberOfRooms = selectedNumberOfRooms
        ? propRooms === selectedRooms
        : true;

      return (
        isWithinLocation &&
        isWithinPriceRange &&
        isOfSelectedPropertyType &&
        isSelectedPropertyAge &&
        isNumberOfRooms
      );
    });
  }, [
    propertyData,
    selectedLocation,
    selectedPropertyType,
    selectedPropertyAge,
    selectedNumberOfRooms,
    priceRange
  ]);

  return (
    <div>
      <PropertyFilterBar
        data={propertyData.filter((p) => p.listingType === "sell")}
        selectedPropertyAge={selectedPropertyAge}
        selectedPropertyType={selectedPropertyType}
        selectedLocation={selectedLocation}
        selectedNumberOfRooms={selectedNumberOfRooms}
        maxPrice={maxPrice}
        priceRange={priceRange}
        setPriceRange={setPriceRange}
        setSelectedLocation={setSelectedLocation}
        setSelectedNumberOfRooms={setSelectedNumberOfRooms}
        setSelectedPropertyAge={setSelectedPropertyAge}
        setSelectedPropertyType={setSelectedPropertyType}
        link={"/explore-to-buy"}
      />

      {isLoading && (
        <div className="flex items-center w-full justify-center">
          <LoadingSpinner />
        </div>
      )}

      {error && (
        <div className="text-red-600 text-center my-4">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {filteredProperties.map((property) => {
          const desc = descriptionsMap[property.id]?.join(", ") || "No description";
          return (
            <Link key={property.id} to={`/explore-to-buy/${property.id}`}>
              <PropertyCard
                id={property.id}
                cover={property.cover}
                location={property.location}
                price={property.price}
                propertyType={property.propertyType}
                description={desc}
                listingType={"buy"}
                status={property.status}
              />
            </Link>
          );
        })}
      </div>

      {!isLoading && filteredProperties.length === 0 && (
        <div className="w-full items-center h-48 flex justify-center">
          <div className="col-span-4 text-center text-[#272c63] font-semibold">
            Relevant data is not found
          </div>
        </div>
      )}
    </div>
  );
};
